import React, { FC, useRef, useState } from "react";
import { Button, Col, Modal } from "react-bootstrap";
import { AnalyticsSchema } from "../../../models/types";
import { DragAndDrop } from "../../editable/DragAndDrop";

interface Props {
    show: boolean;
    updateSchema: (schema: AnalyticsSchema) => void;
    onClose: () => void;
}

export const ImportAnalyticsModal: FC<Props> = ({
    show,
    updateSchema,
    onClose
}) => {
    const inputFileElement = useRef<HTMLInputElement | null>(null);
    const [info, updateInfo] = useState("");
    const [files, updateFiles] = useState<File[]>([]);
    const [fileNames, updateFileNames] = useState<string[]>([]);
    const [submitting, updateSubmitting] = useState(false);

    const addFile = (newFiles: File[]) => {
        if (newFiles.length === 1) {
            updateFiles(newFiles);
            updateFileNames([newFiles[0].name]);
        } else {
            updateInfo("Only one schema can be imported at a time!");
            setTimeout(() => {
                updateInfo("");
            }, 2000);
            updateSubmitting(false);
        }
    };

    const removeFile = (name: string) => {
        const currentFiles = files.filter(file => file.name !== name);
        updateFiles(currentFiles);
        const filenames = currentFiles.map(file => file.name);
        updateFileNames(filenames);
    };

    const submit = async () => {
        for (let i = 0; i < files.length; i++) {
            if (files[i].type !== "application/json") {
                updateInfo("Unsupported file type!");
                setTimeout(() => {
                    updateInfo("");
                }, 2000);
                updateSubmitting(false);
                return;
            }
        }
        try {
            for (let index = 0; index < files.length; index++) {
                const jsonString = await files[index].text();
                const newSchema: AnalyticsSchema = JSON.parse(jsonString);
                updateSchema(newSchema);
            }
            updateFiles([]);
            updateFileNames([]);
            onClose();
        } catch (error) {
            console.log("[DEBUG] submit error ", error);
            // @ts-ignore
            updateInfo(error.message);
            setTimeout(() => {
                updateInfo("");
            }, 4000);
        }

        updateSubmitting(false);
    };

    const onFileLoadClick = () => {
        // @ts-ignore
        inputFileElement.current.click();
    };
    const onChangeFile = (event: any) => {
        event.stopPropagation();
        event.preventDefault();
        const newFiles = event.target.files;

        if (newFiles.length > 0) {
            for (let i = 0; i < newFiles.length; i++) {
                const element = newFiles[i];
                if (element.type !== "application/json") {
                    updateInfo("Unsupported file type!");
                    setTimeout(() => {
                        updateInfo("");
                    }, 2000);
                    return;
                }
            }
        }

        if (newFiles.length === 1) {
            updateFiles(newFiles);
            updateFileNames([newFiles[0].name]);
        } else {
            updateInfo("Only one schema can be imported at a time!");
            setTimeout(() => {
                updateInfo("");
            }, 2000);
            updateSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} backdrop="static" keyboard={true}>
            <Modal.Header closeButton>
                <Col>
                    <h4>Import Schema</h4>
                </Col>
                <Col></Col>
            </Modal.Header>
            <Modal.Body className="p-2">
                <DragAndDrop
                    instructions={
                        <span>
                            You can import schema by{" "}
                            <span className="text-info">dragging it here</span>{" "}
                            or by using the upload file button below. Only JSON
                            files are currently supported.
                        </span>
                    }
                    fileNames={fileNames}
                    addFiles={addFile}
                    removeFile={removeFile}
                />
                <input
                    type="file"
                    id="file"
                    ref={inputFileElement}
                    style={{ display: "none" }}
                    onChange={onChangeFile}
                />
                <span className="d-flex justify-content-center">
                    <Button className="btn-info" onClick={onFileLoadClick}>
                        Upload File
                    </Button>
                </span>
            </Modal.Body>
            <Modal.Footer className="d-flex flex-column justify-content-center align-content-center">
                <h5 className="text-center text-danger">{info}</h5>
                <Button
                    disabled={submitting}
                    onClick={() => {
                        updateSubmitting(true);
                        submit();
                    }}
                >
                    {submitting ? "Submitting..." : "Submit"}
                </Button>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
